import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Route,
  Star,
  Trash2,
  Truck,
  Car,
  Clock,
  MapPin,
  Sparkles,
} from 'lucide-react'

interface SavedRoute {
  id: string
  name: string
  description: string | null
  vehicle_name: string | null
  driver_name: string | null
  costing_type: string
  is_optimized: number
  total_distance: number
  total_duration: number
  waypoint_count: number
  is_favorite: number
  last_used_at: number | null
  created_at: number
}

export function SavedRoutes() {
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['saved-routes'],
    queryFn: async () => {
      const res = await fetch('/api/routing/routes')
      if (!res.ok) throw new Error('Failed to fetch saved routes')
      return res.json() as Promise<{ routes: SavedRoute[] }>
    },
    refetchInterval: 60000,
  })

  const deleteMutation = useMutation({
    mutationFn: async (routeId: string) => {
      const res = await fetch(`/api/routing/routes/${routeId}`, {
        method: 'DELETE'
      })
      if (!res.ok) throw new Error('Failed to delete route')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-routes'] })
    },
  })

  const favoriteMutation = useMutation({
    mutationFn: async (routeId: string) => {
      const res = await fetch(`/api/routing/routes/${routeId}/favorite`, {
        method: 'PATCH'
      })
      if (!res.ok) throw new Error('Failed to toggle favorite')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-routes'] })
    },
  })

  const formatDistance = (meters: number) => {
    const miles = meters / 1609.34
    return miles.toFixed(1) + ' mi'
  }

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`
  }

  const handleDelete = (routeId: string, routeName: string) => {
    if (confirm(`Delete route "${routeName}"?`)) {
      deleteMutation.mutate(routeId)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Route className="h-5 w-5" />
          Saved Routes
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">
            Loading saved routes...
          </div>
        ) : !data?.routes || data.routes.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Route className="h-12 w-12 mx-auto mb-3 opacity-20" />
            <p>No saved routes yet</p>
            <p className="text-sm mt-1">Create and save routes from the Route Planner</p>
          </div>
        ) : (
          <div className="space-y-3">
            {data.routes.map((route) => {
              const CostingIcon = route.costing_type === 'truck' ? Truck : Car
              const lastUsed = route.last_used_at
                ? Math.floor((Date.now() / 1000 - route.last_used_at) / 86400)
                : null

              return (
                <Card key={route.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      {/* Left: Route Info */}
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex items-start gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 flex-shrink-0"
                            onClick={() => favoriteMutation.mutate(route.id)}
                          >
                            <Star
                              className={`h-4 w-4 ${
                                route.is_favorite
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-muted-foreground'
                              }`}
                            />
                          </Button>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold truncate">{route.name}</h3>
                            {route.description && (
                              <p className="text-sm text-muted-foreground">{route.description}</p>
                            )}
                          </div>
                        </div>

                        {/* Stats */}
                        <div className="flex flex-wrap gap-4 text-sm ml-10">
                          {route.total_distance && (
                            <div className="flex items-center gap-1">
                              <Route className="h-4 w-4 text-muted-foreground" />
                              <span>{formatDistance(route.total_distance)}</span>
                            </div>
                          )}
                          {route.total_duration && (
                            <div className="flex items-center gap-1">
                              <Clock className="h-4 w-4 text-muted-foreground" />
                              <span>{formatDuration(route.total_duration)}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            <span>{route.waypoint_count} stops</span>
                          </div>
                        </div>

                        {/* Badges */}
                        <div className="flex flex-wrap gap-2 ml-10">
                          <Badge variant="outline" className="gap-1">
                            <CostingIcon className="h-3 w-3" />
                            {route.costing_type === 'truck' ? 'Truck' : 'Auto'}
                          </Badge>
                          {route.is_optimized === 1 && (
                            <Badge variant="outline" className="gap-1">
                              <Sparkles className="h-3 w-3" />
                              Optimized
                            </Badge>
                          )}
                          {route.vehicle_name && (
                            <Badge variant="outline">{route.vehicle_name}</Badge>
                          )}
                          {lastUsed !== null && (
                            <Badge variant="secondary" className="text-xs">
                              Used {lastUsed === 0 ? 'today' : `${lastUsed}d ago`}
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Right: Actions */}
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        <Button size="sm" variant="outline">
                          View
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(route.id, route.name)}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
